/**
 * App Check Token Utility
 *
 * Retrieves the Firebase App Check token for use in direct HTTP requests
 * to Cloud Functions (onRequest). Callable functions (onCall) get this
 * automatically via the Firebase SDK, but HTTP functions need the token
 * attached manually as an 'X-Firebase-AppCheck' header.
 *
 * Gracefully handles cases where App Check is not initialized (e.g.,
 * missing VITE_RECAPTCHA_SITE_KEY in development).
 */

import { getToken } from 'firebase/app-check';
import { appCheck } from '../lib/firebase';

/**
 * Retrieve the current App Check token string.
 *
 * Returns the token string if available, or null if App Check is not
 * initialized or the token cannot be obtained. Callers should include
 * the token in the 'X-Firebase-AppCheck' header when it is non-null.
 *
 * @param forceRefresh - Force a fresh token instead of using a cached one
 */
export async function getAppCheckToken(forceRefresh = false): Promise<string | null> {
  if (!appCheck) {
    console.warn('App Check not initialized — skipping token for HTTP request');
    return null;
  }

  try {
    const tokenResult = await getToken(appCheck, forceRefresh);
    return tokenResult.token;
  } catch (err) {
    console.warn('Failed to retrieve App Check token:', err);
    return null;
  }
}

/**
 * Build headers object that includes the App Check token (when available)
 * alongside the standard auth and content-type headers.
 *
 * Usage:
 *   const headers = await buildHttpHeaders(authToken);
 *   fetch(url, { headers, ... });
 */
export async function buildHttpHeaders(
  authToken: string,
  contentType = 'application/json'
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': contentType,
    Authorization: `Bearer ${authToken}`,
  };

  const appCheckToken = await getAppCheckToken();
  if (appCheckToken) {
    headers['X-Firebase-AppCheck'] = appCheckToken;
  }

  return headers;
}
